import { IconButton } from 'design-system';
import { DownloadIcon } from 'lucide-react';
import { useCallback } from 'react';

import type { TimeRangeButton } from '@/config/constants';
import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import { useCsvDownloadContext } from '@/features/charts/context/CsvTableContext';
import { useAppDispatch } from '@/store/hooks';
import { openExportDrawer } from '@/store/slices/drawer.slice';

const normalizeSelectedRange = (
  selectedRange: string | undefined,
  metadata: Record<string, object | string> | undefined
) => {
  if (!selectedRange) return selectedRange;
  if (selectedRange === 'mtd' || selectedRange === '1mtd') return 'mtd';
  if (selectedRange === 'ytd' || selectedRange === '1ytd') return 'ytd';

  const buttons = (metadata?.timeRangeButtons as TimeRangeButton[] | undefined) ?? [];
  const matchingButton = buttons.find(button => button.value === selectedRange);
  const normalizedLabel = matchingButton?.label.trim().toUpperCase();

  if (normalizedLabel === 'MTD') return 'mtd';
  if (normalizedLabel === 'YTD') return 'ytd';

  return selectedRange;
};

const CustomDownloadChartButton = ({
  tabName,
  metadata,
  disabled = false
}: {
  tabName: string;
  metadata?: Record<string, object | string>;
  disabled?: boolean;
}) => {
  const { hasPermission } = usePermission();
  const isDisabled = disabled || !hasPermission(Permission.ExportImage);
  const dispatch = useAppDispatch();
  const { selectedRange } = useCsvDownloadContext();
  const normalizedSelectedRange = normalizeSelectedRange(selectedRange, metadata);

  const handleClick = useCallback(() => {
    dispatch(
      openExportDrawer({
        source: 'custom_chart',
        customChartExportContext: {
          metadata: metadata ?? {},
          selectedRange: normalizedSelectedRange,
          tabName
        }
      })
    );
  }, [dispatch, metadata, normalizedSelectedRange, tabName]);

  return (
    <IconButton
      disabled={isDisabled}
      label='Export chart'
      variant='ghost'
      onClick={handleClick}
    >
      <DownloadIcon />
    </IconButton>
  );
};

export default CustomDownloadChartButton;
